<template>
	<ConsentCollectionLayout
		title="Consent Collection Methods"
		caption="From here you can create, edit and manage your consent collection methods"
	>
		<template #content>
			<v-container
				class="fill-height"
			>
				<v-row justify="center">
					<v-col cols="10">
						<div class="cards-grid">
							<div
								v-for="card in visibleCards"
								:key="card.id"
								class="d-flex"
							>
								<OverviewCard
									:title="card.title"
									:description="card.description"
									:icon="card.icon"
									:user-can-create-and-update="card.canCreateUpdate"
									@overview="card.action ? card.action() : $router.push({ name: card.routeName })"
								/>
							</div>
						</div>
					</v-col>
				</v-row>
			</v-container>
		</template>
	</ConsentCollectionLayout>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ConsentCollectionLayout from './consent-collection-layout.vue'
import OverviewCard from '../../../../../shared/components/overview-card.vue'
import {
	PREFERENCE_PAGE_LINKS,
	ACTION_LINKS,
	MANAGE_PREFERENCE_LINK,
	PROGRESSIVE_PROFILING,
	MANAGE_PROGRESSIVE_PROFILE,
	PREFERENCE_WIDGETS,
	MANAGE_PREFERENCE_WIDGET,
	CONSENT_COLLECTION_WIDGETS,
	CONSENT_COLLECTION_PREFERENCE_PAGES,
	MANAGE_CONSENT_COLLECTION_WIDGET,
	MANAGE_CONSENT_COLLECTION_PREFERENCE_PAGE
} from '../../../router/route-names.js'
import { getProgressiveProfiles } from '../../../../../shared/utils/api/progressive-profiling.js'
import { getPreferenceWidgets } from '../../../../../shared/utils/api/preference-widgets.js'
import {
	CAN_CREATE_UPDATE_PROGRESSIVE_PROFILING,
	CAN_SEE_PROGRESSIVE_PROFILING,
	SEE_PREF_WIDGETS_AREA,
	PREF_WIDGETS_AREA_FULL_PERMISSIONS,
	LINKS_AREA_FULL_PERMISSIONS,
	LINKS_PAGE_FULL_PERMISSIONS,
	SEE_LINKS_AREA
} from '../../../../../shared/permissions/admin-portal-permissions.js'
import { enableConsentCollectionPreferencePageOptions, enableConsentCollectionWidgetOptions } from '../../../../../shared/state/config-keys.js'
export default {
	components: {
		ConsentCollectionLayout,
		OverviewCard
	},
	setup () {
		return {
			PREFERENCE_PAGE_LINKS,
			ACTION_LINKS,
			MANAGE_PREFERENCE_LINK,
			PROGRESSIVE_PROFILING,
			MANAGE_PROGRESSIVE_PROFILE,
			PREFERENCE_WIDGETS,
			MANAGE_PREFERENCE_WIDGET,
			CONSENT_COLLECTION_WIDGETS,
			CONSENT_COLLECTION_PREFERENCE_PAGES,
			MANAGE_CONSENT_COLLECTION_WIDGET,
			MANAGE_CONSENT_COLLECTION_PREFERENCE_PAGE,
			enableConsentCollectionPreferencePageOptions,
			enableConsentCollectionWidgetOptions
		}
	},
	data () {
		const ACTION_LINK_TYPE = 1
		const PREFERENCE_PAGE_LINK_TYPE = 3
		return {
			ACTION_LINK_TYPE,
			PREFERENCE_PAGE_LINK_TYPE
		}
	},
	computed: {
		...mapGetters('auth', ['productAreaPermission']),
		cardDescriptors () {
			return [
				{
					id: 'custom-preference-pages',
					title: 'Custom Preference Pages',
					description: 'Build your own Preference Pages fully controlled by your CSS',
					icon: 'mdi-format-float-none',
					routeName: this.PREFERENCE_PAGE_LINKS,
					visible: this.userCanSeePreferencePagesAndActionLinks && this.customPreferencePagesEnabled,
					canCreateUpdate: this.userCanCreateAndUpdatePreferencePagesAndActionLinks
				},
				{
					id: 'drag-drop-preference-pages',
					title: 'Drag-and-Drop Preference Pages',
					description: 'Build your own Preference Pages with our no-code editor',
					icon: 'mdi-format-float-right',
					routeName: this.CONSENT_COLLECTION_PREFERENCE_PAGES,
					visible: this.userCanSeePreferencePagesAndActionLinks && this.dragAndDropPreferencePagesEnabled,
					canCreateUpdate: this.userCanCreateAndUpdatePreferencePagesAndActionLinks
				},
				{
					id: 'embedded-consent-collection',
					title: 'Embedded Consent Collection',
					description: 'Build Consent Collection Points to embed within your existing forms',
					icon: 'mdi-format-list-checkbox',
					routeName: this.PREFERENCE_WIDGETS,
					visible: this.userCanSeeWidgets && this.embeddedConsentCollectionEnabled,
					canCreateUpdate: this.userCanCreateAndUpdateWidgets
				},
				{
					id: 'consent-collection-widget',
					title: 'Consent Collection Widget',
					description: 'Build Consent Collection Widgets that can be applied to your existing pages',
					icon: 'mdi-page-layout-body',
					routeName: this.CONSENT_COLLECTION_WIDGETS,
					visible: this.userCanSeeWidgets && this.consentCollectionWidgetEnabled,
					canCreateUpdate: this.userCanCreateAndUpdateWidgets
				},
				{
					id: 'action-links',
					title: 'Action Links',
					description: 'Create links that set your customers consents and preferences',
					icon: 'mdi-link',
					routeName: this.ACTION_LINKS,
					visible: this.userCanSeePreferencePagesAndActionLinks,
					canCreateUpdate: this.userCanCreateAndUpdatePreferencePagesAndActionLinks
				},
				{
					id: 'progressive-profiling',
					title: 'Progressive Profiling',
					description: 'Create pop-ups (e.g. questions, surveys or polls) to build anonymous preference profiles',
					icon: 'mdi-sitemap',
					routeName: this.PROGRESSIVE_PROFILING,
					visible: this.userCanSeeProgressiveProfiling,
					canCreateUpdate: this.userCanCreateAndUpdateProgressiveProfiling
				}
			]
		},
		visibleCards () {
			return this.cardDescriptors.filter(card => card.visible)
		},
		userCanSeeProgressiveProfiling () {
			return this.productAreaPermission(CAN_SEE_PROGRESSIVE_PROFILING)
		},
		userCanCreateAndUpdateProgressiveProfiling () {
			return this.productAreaPermission(CAN_CREATE_UPDATE_PROGRESSIVE_PROFILING)
		},
		userCanSeeWidgets () {
			return this.productAreaPermission(SEE_PREF_WIDGETS_AREA)
		},
		userCanCreateAndUpdateWidgets () {
			return this.productAreaPermission(PREF_WIDGETS_AREA_FULL_PERMISSIONS)
		},
		userCanSeePreferencePagesAndActionLinks () {
			return this.productAreaPermission(SEE_LINKS_AREA)
		},
		userCanCreateAndUpdatePreferencePagesAndActionLinks () {
			return this.productAreaPermission(LINKS_AREA_FULL_PERMISSIONS) || this.productAreaPermission(LINKS_PAGE_FULL_PERMISSIONS)
		},
		customPreferencePagesEnabled () {
			return ['0', '2'].includes(enableConsentCollectionPreferencePageOptions.value)
		},
		dragAndDropPreferencePagesEnabled () {
			return ['1', '2'].includes(enableConsentCollectionPreferencePageOptions.value)
		},
		embeddedConsentCollectionEnabled () {
			return ['0', '2'].includes(enableConsentCollectionWidgetOptions.value)
		},
		consentCollectionWidgetEnabled () {
			return ['1', '2'].includes(enableConsentCollectionWidgetOptions.value)
		}
	},
	async created () {
		this.loadProgressiveProfiling()
		this.loadPreferenceWidgets()
	},
	methods: {
		...mapActions('lookupData', ['storeProgressiveProfilingLookupData', 'storePreferenceWidgetLookupData']),
		async loadProgressiveProfiling () {
			const { lookupData } = await getProgressiveProfiles()
			this.storeProgressiveProfilingLookupData(lookupData)
		},
		async loadPreferenceWidgets () {
			const { lookupData } = await getPreferenceWidgets()
			this.storePreferenceWidgetLookupData(lookupData)
		}
	}
}
</script>

<style lang="scss" scoped>
.cards-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
	gap: 24px;
	padding: 0;

	@media (min-width: 1264px) {
		grid-template-columns: repeat(3, 1fr);
	}

	@media (max-width: 959px) {
		grid-template-columns: repeat(2, 1fr);
	}

	@media (max-width: 600px) {
		grid-template-columns: 1fr;
	}
}
</style>
